import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Zap, LogIn, UserPlus } from "lucide-react";

export default function AdminLogin() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdf7ff]">
        <div className="animate-spin w-8 h-8 border-4 border-[#a21caf] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else {
      result = await register(email, password, name);
    }
    setLoading(false);
    if (result.success) {
      navigate("/admin");
    } else {
      setError(result.error || "Something went wrong");
    }
  };

  return (
    <div className="min-h-screen bg-[#fdf7ff] flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl bg-[#a21caf] border-2 border-[#111] flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(17,17,17,1)]">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-parkinsans text-lg font-medium">Admin Panel</span>
        </div>

        <div className="nb-card" data-testid="login-form-card">
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2 rounded-lg border-2 border-[#111] font-bold text-sm transition-all ${
                mode === "login"
                  ? "bg-[#a21caf] text-white shadow-[2px_2px_0px_0px_rgba(17,17,17,1)]"
                  : "bg-white text-[#111]"
              }`}
              data-testid="tab-login"
            >
              <LogIn className="w-4 h-4 inline mr-1" strokeWidth={2.5} /> Sign In
            </button>
            <button
              onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2 rounded-lg border-2 border-[#111] font-bold text-sm transition-all ${
                mode === "register"
                  ? "bg-[#a21caf] text-white shadow-[2px_2px_0px_0px_rgba(17,17,17,1)]"
                  : "bg-white text-[#111]"
              }`}
              data-testid="tab-register"
            >
              <UserPlus className="w-4 h-4 inline mr-1" strokeWidth={2.5} /> Register
            </button>
          </div>

          {error && (
            <div className="mb-4 px-4 py-2 rounded-lg bg-[#ef4444]/10 border-2 border-[#ef4444] text-sm text-[#ef4444] font-medium" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-sm font-semibold mb-1.5">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="nb-input"
                  data-testid="register-name"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                className="nb-input"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="nb-input"
                required
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`nb-btn-primary w-full ${loading ? "opacity-70" : ""}`}
              data-testid="login-submit-btn"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          {mode === "register" && (
            <p className="text-xs text-gray-400 mt-4 text-center">
              Registration requires your email to be in the admin allowlist.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
